import React, { useState, useCallback, useRef, useEffect } from 'react';
import Button from '../../../Button/Button';
import ButtonGroup from '../../../Button/ButtonGroup/ButtonGroup';
import TextInput from '../../../TextInput/TextInput';
import Icon from '../../../Icon2/Icon';
import Tooltip from '../../../Tooltip/Tooltip';
import showNotification from '../../../Notification/Notification';

interface CreateFolderDialogProps {
  parentFolderUid: string;
  createFolder: (title: string, parentUid: string) => Promise<{ uid: string; title: string }>;
  onFolderCreated: (folderUid: string, folderTitle: string) => void;
  onCancel: () => void;
}

const CreateFolderDialog: React.FC<CreateFolderDialogProps> = ({
  parentFolderUid,
  createFolder,
  onFolderCreated,
  onCancel,
}) => {
  const [folderName, setFolderName] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    // Focus the input field when the dialog opens
    setTimeout(() => inputRef.current?.focus(), 100);
  }, []);

  const handleCreate = useCallback(async () => {
    const trimmedName = folderName.trim();
    if (!trimmedName) return;

    try {
      setIsCreating(true);
      const result = await createFolder(trimmedName, parentFolderUid);
      setIsCreating(false);
      onFolderCreated(result.uid, result.title || trimmedName);
      showNotification({
        displayContent: { text: 'Folder created successfully' },
        type: 'success',
        dataTestId: 'cs-notification-text',
        notifyProps: { hideProgressBar: true },
      });
    } catch {
      showNotification({
        displayContent: { text: 'Failed to create folder' },
        type: 'error',
      });
      setIsCreating(false);
    }
  }, [folderName, createFolder, parentFolderUid, onFolderCreated]);

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === 'Escape') {
        onCancel();
      }
      if (event.key === 'Enter') {
        handleCreate();
      }
    },
    [handleCreate, onCancel],
  );

  return (
    <div
      className="CreateFolderDialog__overlay"
      onClick={onCancel}
      data-test-id="cs-create-folder-dialog-overlay"
    >
      <div
        className="CreateFolderDialog"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={handleKeyDown}
        data-test-id="cs-create-folder-dialog"
      >
        <div className="CreateFolderDialog__header">
          <span className="CreateFolderDialog__title">Create a New Folder</span>
          <Tooltip content="Create a new folder to organize your assets" position="top">
            <Icon icon="InfoCircleWhite" className="CreateFolderDialog__help-icon" />
          </Tooltip>
        </div>

        <div className="CreateFolderDialog__body">
          <TextInput
            onChange={(e: any) => setFolderName(e.target.value)}
            type="text"
            placeholder="Enter a folder name"
            value={folderName}
            autoFocus={true}
            required={true}
            testId="cs-create-folder-dialog-input"
            ref={inputRef}
            version="v2"
          />
        </div>

        <div className="CreateFolderDialog__footer">
          <Button
            buttonType="tertiary"
            version="v2"
            testId="cs-create-folder-dialog-cancel"
            onClick={onCancel}
            disabled={isCreating}
          >
            Cancel
          </Button>
          <Button
            version="v2"
            testId="cs-create-folder-dialog-create"
            onClick={handleCreate}
            isLoading={isCreating}
            disabled={!folderName.trim()}
          >
            Create Folder
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CreateFolderDialog;
